import { useEffect, useMemo, useState } from "react";
import { useDemoIdentity } from "./DemoIdentityContext";
import {
  analyticsLocations,
  dealersInScope,
  indexOrg,
  regionsInScope,
  DEALER_LEVEL_POLICIES,
  REGION_LEVEL_POLICIES,
} from "./organization";
import { useTenantTerms } from "./TenantConfigContext";
import { defaultRangeFor } from "./dates";
import { toRangeParams } from "@packs/automotive/web/lib/workshopAnalytics";
import { useTenantOrganization } from "./api";

export const ALL = "All Locations";

// Which cars the analytics page covers: the date range plus the workshop,
// dealer and region narrowing this login may use. Same rules as the original
// Workshop Analytics page; the API still drops anything outside the login's
// scope, so these only narrow what is already allowed.
export function useAnalyticsScope() {
  const { role, scopeLocations, workshops } = useDemoIdentity();
  const { canSeePage, roleAccess } = useTenantTerms();
  const allowed = canSeePage(role, "analytics");
  const isSA = (roleAccess(role)?.access_policy ?? (role === "sa" ? "assigned_customers" : null)) === "assigned_customers";

  const [range, setRange] = useState(() => defaultRangeFor("Day"));
  const [loc, setLoc] = useState(ALL);
  const [regionId, setRegionId] = useState(ALL);
  const [dealerId, setDealerId] = useState(ALL);

  const { data: org } = useTenantOrganization(null);
  const orgIx = useMemo(() => indexOrg(org), [org]);
  const policy = role === "master" ? "entire_brand" : roleAccess(role)?.access_policy || null;

  const regionOptions = useMemo(() => regionsInScope(orgIx, scopeLocations), [orgIx, scopeLocations]);
  const showRegionFilter = REGION_LEVEL_POLICIES.has(policy) && regionOptions.length > 0;
  const regionLocations = useMemo(() => {
    if (!showRegionFilter || regionId === ALL) return null;
    return regionOptions.find((r) => String(r.id) === String(regionId))?.locations || null;
  }, [showRegionFilter, regionId, regionOptions]);

  const dealerOptions = useMemo(() => {
    const all = dealersInScope(orgIx, scopeLocations);
    if (!regionLocations) return all;
    const within = new Set(regionLocations);
    return all
      .map((d) => ({ ...d, locations: d.locations.filter((l) => within.has(l)) }))
      .filter((d) => d.locations.length);
  }, [orgIx, scopeLocations, regionLocations]);
  const showDealerFilter = dealerOptions.length > 0 && (DEALER_LEVEL_POLICIES.has(policy) || dealerOptions.length > 1);
  const dealerLocations = useMemo(() => {
    if (!showDealerFilter || dealerId === ALL) return null;
    return dealerOptions.find((d) => String(d.id) === String(dealerId))?.locations || null;
  }, [showDealerFilter, dealerId, dealerOptions]);

  // A selection can stop existing (snapshot loads late, scope changes). Reset it
  // rather than filter by something absent: an empty location list reads as "no
  // filter" at the API and would quietly widen the numbers.
  useEffect(() => {
    if (regionId !== ALL && !regionOptions.some((r) => String(r.id) === String(regionId))) setRegionId(ALL);
  }, [regionId, regionOptions]);
  useEffect(() => {
    if (dealerId !== ALL && !dealerOptions.some((d) => String(d.id) === String(dealerId))) setDealerId(ALL);
  }, [dealerId, dealerOptions]);

  const locationChips = useMemo(() => {
    const narrowed = dealerLocations || regionLocations;
    if (narrowed) return narrowed.length > 1 ? [ALL, ...narrowed] : narrowed;
    if (scopeLocations === "all") return [ALL, ...workshops];
    if (scopeLocations.length <= 1) return scopeLocations;
    return [ALL, ...scopeLocations];
  }, [dealerLocations, regionLocations, scopeLocations, workshops]);
  useEffect(() => {
    if (locationChips.length && !locationChips.includes(loc)) setLoc(locationChips[0]);
  }, [locationChips, loc]);

  const base = useMemo(() => {
    const dates = toRangeParams(range);
    if (!allowed || !dates) return null;
    const locations = analyticsLocations({
      workshop: loc,
      allWorkshopsToken: ALL,
      dealerLocations,
      regionLocations,
      scopeLocations,
    });
    return { ...dates, locations };
  }, [allowed, range, loc, dealerLocations, regionLocations, scopeLocations]);

  return {
    allowed,
    isSA,
    range,
    setRange,
    loc,
    setLoc,
    locationChips,
    regionId,
    setRegionId,
    regionOptions,
    showRegionFilter,
    dealerId,
    setDealerId,
    dealerOptions,
    showDealerFilter,
    base,
    scopeLocations,
  };
}
